import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type {
  CreateEmailTemplatePayload,
  EmailTemplateModel,
  UpdateEmailTemplatePayload,
} from '../models/email-template.model';

@Injectable({ providedIn: 'root' })
export class EmailTemplatesApiService {
  private readonly http = inject(HttpClient);

  async list(): Promise<EmailTemplateModel[]> {
    const raw = await this.tryGet(['/api/email-templates', '/api/company/email-templates']);
    return this.mapList(raw);
  }

  async getById(id: string): Promise<EmailTemplateModel | null> {
    const encoded = encodeURIComponent(id.trim());
    const raw = await this.tryGet([
      `/api/email-templates/${encoded}`,
      `/api/company/email-templates/${encoded}`,
    ]);
    const one = this.mapOne(raw);
    return one?.id ? one : null;
  }

  async create(payload: CreateEmailTemplatePayload): Promise<EmailTemplateModel> {
    const body = this.toWireBody(payload);
    const raw = await firstValueFrom(this.http.post<unknown>('/api/email-templates', body));
    const mapped = this.mapOne(raw);
    if (!mapped?.id) throw new Error('Resposta inválida ao criar template.');
    return mapped;
  }

  async update(id: string, payload: UpdateEmailTemplatePayload): Promise<EmailTemplateModel> {
    const encoded = encodeURIComponent(id.trim());
    const body = this.toWireBody(payload);
    const raw = await firstValueFrom(
      this.http.put<unknown>(`/api/email-templates/${encoded}`, body)
    );
    const mapped = this.mapOne(raw);
    if (!mapped?.id) throw new Error('Resposta inválida ao atualizar template.');
    return mapped;
  }

  async patchActive(id: string, isActive: boolean): Promise<void> {
    const encoded = encodeURIComponent(id.trim());
    try {
      await firstValueFrom(
        this.http.patch(`/api/email-templates/${encoded}`, { isActive })
      );
    } catch (e) {
      if (e instanceof HttpErrorResponse && e.status === 404) {
        await firstValueFrom(
          this.http.put(`/api/email-templates/${encoded}`, { isActive })
        );
        return;
      }
      throw e;
    }
  }

  readConflictKey(error: unknown): string | null {
    if (!(error instanceof HttpErrorResponse)) return null;
    const payload = error.error;
    if (!payload || typeof payload !== 'object') return null;
    const rec = payload as Record<string, unknown>;
    const code = String(rec['code'] ?? rec['Code'] ?? '').toLowerCase();
    if (code.includes('duplicate') || code.includes('key')) {
      return typeof rec['message'] === 'string' ? rec['message'] : null;
    }
    const msg = typeof rec['message'] === 'string' ? rec['message'].toLowerCase() : '';
    if (msg.includes('key') || msg.includes('chave')) return rec['message'] as string;
    return null;
  }

  private toWireBody(p: CreateEmailTemplatePayload): Record<string, unknown> {
    return {
      name: p.name.trim(),
      key: p.key.trim(),
      subject: p.subject.trim(),
      bodyHtml: p.bodyHtml,
      isActive: p.isActive,
    };
  }

  private mapList(raw: unknown): EmailTemplateModel[] {
    if (Array.isArray(raw)) {
      return raw.map((x) => this.mapOne(x)).filter((x): x is EmailTemplateModel => !!x?.id);
    }
    if (raw && typeof raw === 'object') {
      const rec = raw as Record<string, unknown>;
      const items = rec['items'] ?? rec['Items'] ?? rec['data'];
      if (Array.isArray(items)) {
        return items.map((x) => this.mapOne(x)).filter((x): x is EmailTemplateModel => !!x?.id);
      }
    }
    return [];
  }

  private mapOne(raw: unknown): EmailTemplateModel | null {
    if (!raw || typeof raw !== 'object') return null;
    const r = raw as Record<string, unknown>;
    const id = this.str(r, ['id', 'Id']);
    if (!id) return null;
    return {
      id,
      name: this.str(r, ['name', 'Name', 'title', 'Title']) || id,
      key: this.str(r, ['key', 'Key', 'templateKey', 'TemplateKey']) || '',
      subject: this.str(r, ['subject', 'Subject']) || '',
      bodyHtml: this.str(r, ['bodyHtml', 'BodyHtml', 'body', 'Body', 'htmlBody']) || '',
      isActive: this.bool(r, ['isActive', 'IsActive'], true),
      updatedAt: this.str(r, ['updatedAt', 'UpdatedAt']) || undefined,
    };
  }

  private str(r: Record<string, unknown>, keys: string[]): string {
    for (const k of keys) {
      const v = r[k];
      if (typeof v === 'string') return v;
    }
    return '';
  }

  private bool(r: Record<string, unknown>, keys: string[], fallback: boolean): boolean {
    for (const k of keys) {
      const v = r[k];
      if (typeof v === 'boolean') return v;
    }
    return fallback;
  }

  private async tryGet(paths: string[]): Promise<unknown | null> {
    for (const path of paths) {
      try {
        return await firstValueFrom(this.http.get<unknown>(path));
      } catch {
        // tenta próximo
      }
    }
    return null;
  }
}
